import { inject, Injectable } from '@angular/core';
import { SqliteService, StorageService } from '@app/services';
import { UserModel } from '@app/models';
import { environment } from '@env/environment';

@Injectable({
    providedIn: 'root',
})
export class UserRepository
{
    private readonly keys = {
        loggedInUser: 'logged_in_user',
        authToken: 'auth_token',
        clearLocalStorageKey: 'clear_local_storage_key',
        clearDatabaseKey: 'clear_database_key',
        tableName: 'users',
    };
    private storageService = inject(StorageService);
    private sqliteService = inject(SqliteService);

    async getLoggedInUser(): Promise<UserModel | null>
    {
        return await this.storageService.get<UserModel>(this.keys.loggedInUser);
    }

    async clearLoggedInUser(): Promise<void>
    {
        await this.storageService.remove(this.keys.loggedInUser);
    }

    async getAuthToken(): Promise<string | null>
    {
        return await this.storageService.get<string>(this.keys.authToken);
    }

    async clearAuthToken(): Promise<void>
    {
        await this.storageService.remove(this.keys.authToken);
    }

    async setLoggedInUser(user: UserModel): Promise<void>
    {
        await this.storageService.set<UserModel>(this.keys.loggedInUser, user);
        const valuePlaceholders = this.sqliteService.getValuePlaceholder(1, 5);
        const sql = `
            INSERT INTO ${this.keys.tableName} (id, name, external_id, email, bu)
            VALUES ${valuePlaceholders} ON CONFLICT(id) DO
            UPDATE
                SET
                    id = excluded.id,
                external_id = excluded.external_id,
                email = excluded.email,
                bu = excluded.bu,
                name = excluded.name;
        `;

        await this.sqliteService.save(sql, [user.id, user.name, user.external_id, user.email, user.bu]);
    }

    async setAuthToken(token: string): Promise<void>
    {
        await this.storageService.set(this.keys.authToken, token);
    }

    async login(user: UserModel, token: string): Promise<void>
    {
        await this.setLoggedInUser(user);
        await this.setAuthToken(token);
    }

    async getUsers(): Promise<UserModel[]>
    {
        const sql = `
            SELECT *
            FROM ${this.keys.tableName};
        `;

        const data = await this.sqliteService.get(sql) ?? [];

        return data as UserModel[];
    }

    async logout(): Promise<void>
    {
        // await this.clearAuthToken();
        // await this.clearLoggedInRep();
        await this.storageService.clear();

        await this.storageService.set<string>(this.keys.clearLocalStorageKey, environment.clearLocalStorageKey);
        await this.storageService.set<string>(this.keys.clearDatabaseKey, environment.clearDatabaseKey);
    }
}
